/**
 * Upload ảnh nhiều tấm cho tab Khách hàng (kind='owner', tối đa 5) và
 * tab Thanh toán (kind='payment', tối đa 3 mỗi khoản).
 *
 * CHỐNG HACK (phối hợp với backend cmd/mdmquery/images.go):
 *   - Client: chỉ nhận đuôi ảnh cơ bản (jpg/png/gif/webp/bmp), vẽ qua <canvas>
 *     rồi RE-ENCODE về JPEG. Việc này (a) loại metadata/payload nhúng,
 *     (b) resize khi ảnh quá lớn để giảm dung lượng. Trình duyệt chỉ giải mã
 *     được ảnh thật, nên file giả mạo (html/script đổi đuôi) sẽ hỏng ở bước này.
 *   - Server vẫn decode + re-encode + giới hạn kích thước/số điểm ảnh/số lượng
 *     (không tin client). Ảnh lưu trong DB (BYTEA), phục vụ với nosniff + inline.
 */
import { useEffect, useRef, useState } from 'react';
import * as query from '../api/query';
import { confirmVN } from './Dialog';

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,image/bmp';
const MAX_DIM = 1600;      // cạnh dài tối đa sau resize (px)
const JPEG_QUALITY = 0.82; // chất lượng re-encode
const MAX_SOURCE_BYTES = 25 * 1024 * 1024; // chặn file khổng lồ trước khi decode

// resizeToJpeg: đọc File ảnh -> vẽ ra canvas (thu nhỏ nếu vượt MAX_DIM) -> Blob JPEG.
// Trả về Promise<Blob>. Lỗi nếu trình duyệt không giải mã được (file không phải ảnh).
export function resizeToJpeg(file, { maxDim = MAX_DIM, quality = JPEG_QUALITY } = {}) {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_SOURCE_BYTES) { reject(new Error('Ảnh quá lớn (tối đa 25MB)')); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width: w, height: h } = img;
      if (!w || !h) { reject(new Error('Ảnh không hợp lệ')); return; }
      const scale = Math.min(1, maxDim / Math.max(w, h));
      w = Math.round(w * scale);
      h = Math.round(h * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Không nén được ảnh'))),
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Không đọc được ảnh (sai định dạng?)')); };
    img.src = url;
  });
}

/**
 * ImageGallery — upload NGAY (dùng cho tab Khách hàng, và cho khoản thanh toán ĐÃ tồn tại).
 * Tự tải danh sách ảnh hiện có theo (kind[, paymentId]).
 */
export function ImageGallery({ udid, kind, paymentId, max, canDelete = true, showToast, compact = false }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [viewer, setViewer] = useState(null); // index ảnh đang xem lớn (lightbox), null = đóng
  const inputRef = useRef(null);

  const load = () =>
    query.listImages(udid, kind)
      .then((all) => {
        const list = (all || []).filter((m) => (kind === 'payment' ? Number(m.payment_id) === Number(paymentId) : true));
        setItems(list);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [udid, kind, paymentId]);

  const toast = (msg, type, icon) => showToast && showToast(msg, type, icon);

  async function onPick(e) {
    const files = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = ''; // cho phép chọn lại cùng file
    if (!files.length) return;
    const room = max - items.length;
    if (room <= 0) { toast(`Tối đa ${max} ảnh`, 'error', '❌'); return; }
    const pick = files.slice(0, room);
    if (files.length > room) toast(`Chỉ thêm được ${room} ảnh nữa (tối đa ${max})`, 'error', '⚠️');
    setBusy(true);
    try {
      for (const file of pick) {
        try {
          const blob = await resizeToJpeg(file);
          await query.uploadImage(udid, { kind, paymentId, blob, name: file.name });
        } catch (ex) {
          toast((ex.body && ex.body.error) || ex.message || 'Lỗi upload ảnh', 'error', '❌');
        }
      }
      await load();
    } finally { setBusy(false); }
  }

  async function del(imgId) {
    if (!(await confirmVN('Xóa ảnh này?', { title: 'Xóa ảnh', okText: 'Xóa', danger: true }))) return;
    try { await query.deleteImage(udid, imgId); await load(); toast('Đã xóa ảnh', 'success', '🗑'); }
    catch (ex) { toast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  const full = items.length >= max;
  return (
    <div className={`img-gallery${compact ? ' img-gallery-compact' : ''}`}>
      {items.map((m, i) => (
        <div className="img-thumb" key={m.id} title={m.name || ''} role="button" tabIndex={0}
          onClick={() => setViewer(i)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewer(i); } }}>
          <img src={query.imageURL(udid, m.id)} alt={m.name || 'ảnh'} loading="lazy" />
          {canDelete && (
            <button type="button" className="img-del" title="Xóa ảnh"
              onClick={(e) => { e.stopPropagation(); del(m.id); }}>×</button>
          )}
        </div>
      ))}
      {!full && (
        <button type="button" className="img-add" disabled={busy} onClick={() => inputRef.current && inputRef.current.click()}>
          {busy ? '⏳' : <><span className="img-add-plus">+</span><span className="img-add-txt">{loaded ? `Ảnh (${items.length}/${max})` : 'Ảnh'}</span></>}
        </button>
      )}
      <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden onChange={onPick} />
      {viewer !== null && items.length > 0 && (
        <Lightbox
          items={items.map((m) => ({ src: query.imageURL(udid, m.id), name: m.name }))}
          index={Math.min(viewer, items.length - 1)}
          onClose={() => setViewer(null)}
          onIndex={setViewer}
        />
      )}
    </div>
  );
}

/**
 * Lightbox — xem ảnh lớn, lướt qua lại nhiều ảnh. Phủ toàn màn hình (trên cả
 * drawer). Điều hướng: nút ‹ ›, phím ← →, đóng bằng × / Esc / bấm nền tối.
 * items: [{ src, name }].
 */
export function Lightbox({ items, index, onClose, onIndex }) {
  const n = items.length;
  const go = (d) => onIndex(((index + d) % n + n) % n);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [index, n]);
  if (!n) return null;
  const cur = items[index] || items[0];
  const many = n > 1;
  return (
    <div className="img-lightbox" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <button type="button" className="lb-close" title="Đóng (Esc)" onClick={onClose}>×</button>
      {many && <button type="button" className="lb-nav lb-prev" title="Trước (←)" onClick={() => go(-1)}>‹</button>}
      <div className="lb-stage" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <img className="lb-img" src={cur.src} alt={cur.name || ''} />
        <div className="lb-cap">{cur.name ? cur.name + ' · ' : ''}{index + 1}/{n}</div>
      </div>
      {many && <button type="button" className="lb-nav lb-next" title="Sau (→)" onClick={() => go(1)}>›</button>}
    </div>
  );
}

/**
 * PendingImagePicker — GIỮ ảnh (đã resize) trong bộ nhớ trước khi có id.
 * Dùng cho hàng "thêm khoản thanh toán" mới: chọn ảnh trước, upload sau khi
 * khoản được tạo (có id SERIAL). Gọi onChange(blobs[]) mỗi khi đổi.
 */
export function PendingImagePicker({ max, onChange, showToast }) {
  const [previews, setPreviews] = useState([]); // {url, blob, name}
  const [viewer, setViewer] = useState(null);
  const inputRef = useRef(null);
  const toast = (msg, type, icon) => showToast && showToast(msg, type, icon);

  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  const emit = (list) => { setPreviews(list); onChange && onChange(list.map((p) => ({ blob: p.blob, name: p.name }))); };

  async function onPick(e) {
    const files = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = '';
    if (!files.length) return;
    const room = max - previews.length;
    if (room <= 0) { toast(`Tối đa ${max} ảnh`, 'error', '❌'); return; }
    const pick = files.slice(0, room);
    if (files.length > room) toast(`Chỉ thêm được ${room} ảnh nữa (tối đa ${max})`, 'error', '⚠️');
    const added = [];
    for (const file of pick) {
      try {
        const blob = await resizeToJpeg(file);
        added.push({ url: URL.createObjectURL(blob), blob, name: file.name });
      } catch (ex) { toast(ex.message || 'Lỗi đọc ảnh', 'error', '❌'); }
    }
    if (added.length) emit([...previews, ...added]);
  }

  const remove = (idx) => {
    const p = previews[idx];
    if (p) URL.revokeObjectURL(p.url);
    emit(previews.filter((_, i) => i !== idx));
  };

  const full = previews.length >= max;
  return (
    <div className="img-gallery img-gallery-compact">
      {previews.map((p, i) => (
        <div className="img-thumb" key={i} title={p.name} role="button" tabIndex={0}
          onClick={() => setViewer(i)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewer(i); } }}>
          <img src={p.url} alt={p.name} />
          <button type="button" className="img-del" title="Bỏ ảnh" onClick={(e) => { e.stopPropagation(); remove(i); }}>×</button>
        </div>
      ))}
      {!full && (
        <button type="button" className="img-add" onClick={() => inputRef.current && inputRef.current.click()}>
          <span className="img-add-plus">+</span><span className="img-add-txt">Ảnh ({previews.length}/{max})</span>
        </button>
      )}
      <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden onChange={onPick} />
      {viewer !== null && previews.length > 0 && (
        <Lightbox
          items={previews.map((p) => ({ src: p.url, name: p.name }))}
          index={Math.min(viewer, previews.length - 1)}
          onClose={() => setViewer(null)}
          onIndex={setViewer}
        />
      )}
    </div>
  );
}

// CSS cho gallery — nhúng 1 lần (id cố định để không lặp).
export const IMAGE_CSS = `
.dev-drawer .img-gallery{display:flex;flex-wrap:wrap;gap:10px;align-items:flex-start}
.dev-drawer .img-gallery-compact{gap:7px}
.dev-drawer .img-thumb{position:relative;width:92px;height:92px;border-radius:10px;overflow:hidden;border:1px solid var(--border);background:var(--bg3);display:block;flex:none;cursor:pointer;transition:transform .12s,border-color .12s}
.dev-drawer .img-thumb:hover{border-color:var(--blue);transform:translateY(-1px)}
.dev-drawer .img-thumb:focus-visible{outline:2px solid var(--blue);outline-offset:2px}
.dev-drawer .img-gallery-compact .img-thumb{width:64px;height:64px;border-radius:8px}
.dev-drawer .img-thumb img{width:100%;height:100%;object-fit:cover;display:block}
.dev-drawer .img-del{position:absolute;top:3px;right:3px;width:20px;height:20px;border:none;border-radius:50%;background:rgba(18,21,43,.66);color:#fff;font-size:15px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;transition:background .15s}
.dev-drawer .img-del:hover{background:var(--red)}
.dev-drawer .img-add{width:92px;height:92px;border:1.5px dashed var(--border2);border-radius:10px;background:var(--bg3);color:var(--text3);cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;font-size:12px;font-family:inherit;transition:all .16s;flex:none}
.dev-drawer .img-gallery-compact .img-add{width:64px;height:64px;border-radius:8px;font-size:11px}
.dev-drawer .img-add:hover:not(:disabled){border-color:var(--blue);color:var(--blue);background:var(--blue-bg)}
.dev-drawer .img-add:disabled{opacity:.5;cursor:default}
.dev-drawer .img-add-plus{font-size:23px;line-height:1;font-weight:300}
.dev-drawer .img-gallery-compact .img-add-plus{font-size:19px}

/* Lightbox xem ảnh lớn — phủ toàn màn hình, trên cả drawer/confirm */
.dev-drawer .img-lightbox{position:fixed;inset:0;z-index:300;background:rgba(10,12,24,.86);display:flex;align-items:center;justify-content:center;animation:mFadeIn .18s ease}
.dev-drawer .lb-stage{position:relative;max-width:92vw;max-height:92vh;display:flex;flex-direction:column;align-items:center;gap:10px;padding:8px}
.dev-drawer .lb-img{max-width:92vw;max-height:82vh;object-fit:contain;border-radius:8px;background:#fff;box-shadow:0 16px 48px -10px rgba(0,0,0,.6)}
.dev-drawer .lb-cap{color:#e8eaf2;font-size:13px;font-family:'JetBrains Mono',monospace;text-align:center;max-width:92vw;word-break:break-all}
.dev-drawer .lb-close{position:fixed;top:16px;right:20px;width:44px;height:44px;border:none;border-radius:50%;background:rgba(255,255,255,.14);color:#fff;font-size:27px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .15s}
.dev-drawer .lb-close:hover{background:rgba(255,255,255,.28)}
.dev-drawer .lb-nav{position:fixed;top:50%;transform:translateY(-50%);width:52px;height:52px;border:none;border-radius:50%;background:rgba(255,255,255,.14);color:#fff;font-size:35px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .15s}
.dev-drawer .lb-nav:hover{background:rgba(255,255,255,.28)}
.dev-drawer .lb-prev{left:16px}
.dev-drawer .lb-next{right:16px}
@media(max-width:560px){.dev-drawer .lb-nav{width:40px;height:40px;font-size:27px}.dev-drawer .lb-prev{left:8px}.dev-drawer .lb-next{right:8px}}
`;
